import {Box, Button, CircularProgress, Divider, IconButton, Typography, useTheme} from "@mui/material";
import {useEffect, useState} from "react";
import {FiEdit2, FiTrash2} from "react-icons/fi";
import {useLocation, useNavigate} from "react-router-dom";
import UserForm from "../components/Users/UserForm";
import UserInfo from "../components/Users/UserInfo";
import UserTenantsGrid from "../components/Users/UserTenantsGrid";
import Window from "../components/Window/Window";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import useApiRequest from "../hooks/useApiRequest";
import useConfirmDialog from "../hooks/useConfirmDialog";
import UserTokensGrid from "../components/Users/UserTokensGrid";
import {useAuthPermissions, ACTIONS, RESOURCES} from "../hooks/useAuthPermissions";
import {snackbar} from "../utils/snackbar";
import {getApiErrorMessage} from "../utils/apiError";
import apiClient from "../utils/apiClient";

const UserDetail = () => {
  const {state} = useLocation();
  const navigate = useNavigate();
  const id = state?.id;

  const isNewUser = !id;

  const {data, loading, read, remove} = useApiRequest({baseUrl: `/users`, resource: "users"});

  const [isEditing, setIsEditing] = useState(isNewUser);

  const theme = useTheme();
  const {can} = useAuthPermissions();

  // Hook para el diálogo de confirmación de borrado
  const {confirmOpen, itemName, openDialog, handleConfirm, handleCancel} = useConfirmDialog(async () => {
    const {success} = await remove(id);
    if (!success) return;

    snackbar.success("Usuario eliminado correctamente.");
    navigate("/users");
  });

  // Hook para el diálogo de confirmación de reset de TS ID
  const {
    confirmOpen: resetTsIdConfirmOpen,
    openDialog: openResetTsIdDialog,
    handleConfirm: handleResetTsIdConfirm,
    handleCancel: handleResetTsIdCancel
  } = useConfirmDialog(async () => {
    try {
      await apiClient.post(`/users/${id}/reset-ts-id`);
      snackbar.success("TS ID del usuario reiniciado exitosamente.");
      await read(id);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 400) {
        snackbar.error("El usuario no tiene un TS email asociado.");
      } else if (status === 404) {
        snackbar.error("Usuario no encontrado.");
      } else {
        snackbar.error(getApiErrorMessage(error, "Error al reiniciar el TS ID. Inténtalo de nuevo."));
      }
    }
  });

  useEffect(() => {
    if (!isNewUser) {
      read(id);
    }
    // eslint-disable-next-line
  }, [id]);

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleOpenDeleteDialog = () => {
    openDialog(id, data?.name || "");
  };

  const handleOpenResetTsIdDialog = () => {
    openResetTsIdDialog(id, data?.name || "");
  };

  return (
    <>
      <Window
        title={isNewUser ? "Nuevo Usuario" : "Detalles del Usuario"}
        backTo="/users"
        topRightButtons={
          <>
            {!isNewUser && !isEditing && can(ACTIONS.WRITE, RESOURCES.USERS) && (
              <Button variant="contained" color="warning" onClick={handleOpenResetTsIdDialog}>
                Reiniciar TS ID
              </Button>
            )}

            {!isNewUser && can(ACTIONS.WRITE, RESOURCES.USERS) && (
              <Box component="span">
                {isEditing ? (
                  <Button variant="contained" color="primary" onClick={handleEditToggle}>
                    Cancelar
                  </Button>
                ) : (
                  <IconButton aria-label="edit" color="primary" onClick={handleEditToggle}>
                    <FiEdit2 />
                  </IconButton>
                )}

                {can(ACTIONS.DELETE, RESOURCES.USERS) && (
                  <IconButton aria-label="delete" color="error" onClick={handleOpenDeleteDialog}>
                    <FiTrash2 />
                  </IconButton>
                )}
              </Box>
            )}
          </>
        }
      >
        {loading && !isNewUser ? (
          <CircularProgress />
        ) : (
          <>
            <Box sx={{mb: 4}}>
              {isEditing && can(ACTIONS.WRITE, RESOURCES.USERS) ? (
                <UserForm
                  isNew={isNewUser}
                  canManageTsEmail={can(ACTIONS.WRITE, RESOURCES.USERS)}
                  canEditAllFields={can(ACTIONS.WRITE, RESOURCES.USERS)}
                  refreshData={() => read(id)}
                  setIsEditing={setIsEditing}
                  initialUserData={data}
                />
              ) : (
                <UserInfo user={data} />
              )}
            </Box>

            {!isNewUser && (
              <>
                <Typography variant="h3" fontWeight="bold" sx={{color: theme.palette.text.title, mt: 4, mb: 2}}>
                  Tenants y Roles
                </Typography>
                <Divider sx={{mb: 2, borderColor: theme.palette.text.divider}} />
                <UserTenantsGrid userId={id} readOnly={!can(ACTIONS.WRITE, RESOURCES.USER_ROLES)} />
              </>
            )}

            {!isNewUser && (
              <>
                <Typography variant="h3" fontWeight="bold" sx={{color: theme.palette.text.title, mt: 4, mb: 2}}>
                  Tokens
                </Typography>
                <Divider sx={{mb: 2, borderColor: theme.palette.text.divider}} />
                <UserTokensGrid userId={id} />
              </>
            )}
          </>
        )}
      </Window>

      <ConfirmDialog
        open={confirmOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        entityName="usuario"
        entityLabel={itemName}
      />

      <ConfirmDialog
        open={resetTsIdConfirmOpen}
        onClose={handleResetTsIdCancel}
        onConfirm={handleResetTsIdConfirm}
        entityName="TS ID"
        entityLabel={`del usuario ${data?.name || ""}`}
      />
    </>
  );
};

export default UserDetail;
